import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import EmptyStateIcon from '@assets/icons/table/results-empty-state.svg';
import { BaseButton, BaseCheckbox, BaseTag } from '@components';
import { BaseInput } from '@components';
import { MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS } from '@constants/table.ts';
import { useLingui } from '@lingui/react/macro';
import {
    ButtonsWrapper,
    CheckboxLabelWrapper,
    CustomCategoryFilterWrapper as Wrapper,
    EmptyStateText,
    EmptyStateWrapper,
    LabelItemsSection,
    LabelWrapper,
    LevelLabelLight,
    LevelLabelRegular,
    SelectedCountSectionVessels,
    SubItemCategoryLabel,
    SubItemsSection,
    SubitemWrapper,
    ValueGroupHeader,
    ValuesGroupSection,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/styled.tsx';
import type {
    CustomRenderProps,
    TransformedSubItemForMatrices,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/types.ts';
import {
    getNextSelectedCategories,
    getNextSelectedTypes,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/utils.ts';
import {
    getNextCheckedSubItems,
    getNextSelectedGroupsAndTypes,
    getNormalizedSubItems,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/vessels/utils.ts';

/*
<CustomCategorySelectorVessels
    levelOneLabel={'VESSEL GROUP'}
    levelTwoLabel={'VESSEL TYPE'}
    levelOneItems={[
        { value: 'a', label: 'Group A' },
        { value: 'b', label: 'Group B' },
        { value: 'c', label: 'Group C' },
    ]}
    levelTwoItems={[
        { value: 't', label: 'TNK' },
        { value: 'c', label: 'CONT' },
        { value: 'b', label: 'BULK' },
    ]}
    searchText={'Search subtypes...'}
    subItems={[
        { value: 'tnk-oil', label: 'TNK-OIL', type: 't', category: 'a' },
        { value: 'tnk-lng', label: 'TNK-LNG', type: 't', category: 'a' },
        { value: 'tnk-lng1', label: 'TNK-LNG', type: 't', category: 'b' },
        { value: 'cont-ulcv', label: 'CONT-ULCV', type: 'c', category: 'b' },
        { value: 'cont-ulcv1', label: 'CONT-ULCV', type: 'c', category: 'c' },
        { value: 'bulk-hmax', label: 'BULK-HMAX', type: 'b', category: 'b' },
        { value: 'bulk-hmax1', label: 'BULK-HMAX', type: 'b', category: 'c' },
        { value: 'bulk-hmax2', label: 'BULK-HMAX', type: 'b', category: 'a' },
    ]}
    selectedSubItems={[]}
/>
 */

export const VesselsCustomRender: React.FC<CustomRenderProps> = ({
    levelOneLabel,
    levelTwoLabel,
    levelOneItems,
    levelTwoItems,
    subItems,
    searchText,
    selectedSubItems,
    deselectOnly,
    initiallySelectedSubItems,
    onCancel,
    onDone,
}) => {
    const { t } = useLingui();

    const CLICK_TO_SELECT_ALL = t`Click to select all`;

    const [searchValue, setSearchValue] = useState('');
    const [selectedCategoryValues, setSelectedCategoryValues] = useState<string[]>([]);
    const [selectedTypeValues, setSelectedTypeValues] = useState<string[]>([]);
    const [checkedSubItemValues, setCheckedSubItemValues] = useState<Set<string>>(new Set());

    const [isGroupLabelFilterActive, setIsGroupLabelFilterActive] = useState(false);
    const [isTypeLabelFilterActive, setIsTypeLabelFilterActive] = useState(false);

    const allSubItems = useMemo(
        () => getNormalizedSubItems(subItems, levelOneItems, levelTwoItems),
        [subItems, levelOneItems, levelTwoItems],
    );

    const allowedSet = useMemo(() => new Set(initiallySelectedSubItems.map(String)), [initiallySelectedSubItems]);
    const isAllowedSubItem = (value: string) => !deselectOnly || allowedSet.has(String(value));

    const allowedSubItems = useMemo(
        () => (deselectOnly ? allSubItems.filter((subItem) => isAllowedSubItem(subItem.value)) : allSubItems),
        [deselectOnly, allSubItems, allowedSet],
    );

    const hasAllowedGroupItems = (groupValue: string) =>
        allowedSubItems.some((subItem) => subItem.category === groupValue);
    const hasAllowedTypeItems = (typeValue: string) => allowedSubItems.some((subItem) => subItem.type === typeValue);

    const filteredSubItems = useMemo(() => {
        const searchQuery = searchValue.trim().toLowerCase();
        if (!searchQuery) return allSubItems;

        return allSubItems.filter((subItem: TransformedSubItemForMatrices) =>
            subItem.label.toLowerCase().includes(searchQuery),
        );
    }, [allSubItems, searchValue]);

    const handleLabelFilterState = (isLevelOneLabel: boolean, groups: string[], types: string[]) => {
        if (isLevelOneLabel) {
            setIsGroupLabelFilterActive(true);
        } else {
            setIsTypeLabelFilterActive(true);
        }

        if (isLevelOneLabel && groups.length === 0) {
            setIsGroupLabelFilterActive(false);
        }

        if (!isLevelOneLabel && types.length === 0) {
            setIsTypeLabelFilterActive(false);
        }
    };

    const handleLabelClick = (isLevelOneLabel: boolean, labelValue: string) => {
        if (deselectOnly) {
            const hasAllowedSubItems = isLevelOneLabel
                ? hasAllowedGroupItems(labelValue)
                : hasAllowedTypeItems(labelValue);

            if (!hasAllowedSubItems) return;
        }

        const { groups, types } = getNextSelectedGroupsAndTypes(
            selectedCategoryValues,
            selectedTypeValues,
            isLevelOneLabel,
            labelValue,
        );

        setSelectedCategoryValues(groups);
        setSelectedTypeValues(types);

        handleLabelFilterState(isLevelOneLabel, groups, types);

        if (groups.length === 0 && types.length === 0) {
            setCheckedSubItemValues(new Set());

            return;
        }

        const nextCheckedSubItems = getNextCheckedSubItems(
            groups,
            types,
            levelOneItems,
            levelTwoItems,
            allowedSubItems,
        );

        setCheckedSubItemValues(nextCheckedSubItems);
    };

    const handleSelectAll = () => {
        const groupsSelected = isGroupLabelFilterActive && selectedCategoryValues.length > 0;
        const typesSelected = isTypeLabelFilterActive && selectedTypeValues.length > 0;

        const activeGroups = groupsSelected
            ? new Set(selectedCategoryValues)
            : new Set(levelOneItems.map((g) => g.value));
        const activeTypes = typesSelected ? new Set(selectedTypeValues) : new Set(levelTwoItems.map((t) => t.value));

        const nextCheckedSubItemValues = new Set(checkedSubItemValues);

        filteredSubItems
            .filter((subItem) => activeGroups.has(subItem.category) && activeTypes.has(subItem.type))
            .filter((subItem) => isAllowedSubItem(subItem.value))
            .forEach((subItem) => nextCheckedSubItemValues.add(subItem.value));

        const nextSelectedCategories = getNextSelectedCategories(
            levelOneItems,
            allowedSubItems,
            nextCheckedSubItemValues,
        );
        const nextSelectedTypes = getNextSelectedTypes(levelTwoItems, allowedSubItems, nextCheckedSubItemValues);

        setCheckedSubItemValues(nextCheckedSubItemValues);
        setSelectedCategoryValues(nextSelectedCategories);
        setSelectedTypeValues(nextSelectedTypes);
    };

    const handleClearAll = () => {
        setIsGroupLabelFilterActive(false);
        setIsTypeLabelFilterActive(false);

        setCheckedSubItemValues(new Set());
        setSelectedCategoryValues([]);
        setSelectedTypeValues([]);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    const handleCheckboxChange = (subItemValue: string, nextChecked: boolean) => {
        setCheckedSubItemValues((prev) => {
            const nextCheckedSubItemValues = new Set(prev);

            if (nextChecked) {
                nextCheckedSubItemValues.add(subItemValue);
            } else {
                nextCheckedSubItemValues.delete(subItemValue);
            }

            const nextSelectedTypes = getNextSelectedTypes(levelTwoItems, allowedSubItems, nextCheckedSubItemValues);

            const nextSelectedCategories = getNextSelectedCategories(
                levelOneItems,
                allowedSubItems,
                nextCheckedSubItemValues,
            );

            setSelectedCategoryValues(nextSelectedCategories);
            setSelectedTypeValues(nextSelectedTypes);

            return nextCheckedSubItemValues;
        });
    };

    const getSubitemCategoryLabel = (subItemValue: string) => {
        const match = levelOneItems.find((item) => item.value === subItemValue);

        return match?.label ?? '';
    };

    const handleCancel = () => {
        setIsGroupLabelFilterActive(false);
        setIsTypeLabelFilterActive(false);

        setCheckedSubItemValues(new Set(selectedSubItems));
        onCancel();
    };

    const handleDone = () => {
        onDone([...checkedSubItemValues]);
    };

    useEffect(() => {
        setIsGroupLabelFilterActive(false);
        setIsTypeLabelFilterActive(false);

        const selectedSet = new Set(selectedSubItems.map(String));

        const nextSelectedCategories = getNextSelectedCategories(levelOneItems, allowedSubItems, selectedSet);
        const nextSelectedTypes = getNextSelectedTypes(levelTwoItems, allowedSubItems, selectedSet);

        setSelectedCategoryValues(nextSelectedCategories);
        setSelectedTypeValues(nextSelectedTypes);
        setCheckedSubItemValues(new Set(selectedSubItems));
    }, [selectedSubItems, allowedSubItems, levelOneItems, levelTwoItems]);

    return (
        <Wrapper $isFixedWidth>
            <LabelItemsSection>
                <div>
                    <LevelLabelRegular>{levelOneLabel}</LevelLabelRegular>
                    <LevelLabelLight> {`(${CLICK_TO_SELECT_ALL})`}</LevelLabelLight>
                </div>
                <div>
                    {levelOneItems.map((item) => (
                        <LabelWrapper
                            $isSelected={selectedCategoryValues.includes(item.value)}
                            onClick={() => handleLabelClick(true, item.value)}
                            key={item.value}
                        >
                            <BaseTag className={MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS}>{item.label}</BaseTag>
                        </LabelWrapper>
                    ))}
                </div>
            </LabelItemsSection>
            <LabelItemsSection>
                <div>
                    <LevelLabelRegular>{levelTwoLabel}</LevelLabelRegular>
                    <LevelLabelLight> {`(${CLICK_TO_SELECT_ALL})`}</LevelLabelLight>
                </div>
                <div>
                    {levelTwoItems.map((item) => (
                        <LabelWrapper
                            $isSelected={selectedTypeValues.includes(item.value)}
                            onClick={() => handleLabelClick(false, item.value)}
                            key={item.value}
                        >
                            <BaseTag className={MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS}>{item.label}</BaseTag>
                        </LabelWrapper>
                    ))}
                </div>
            </LabelItemsSection>
            <ButtonsWrapper>
                <BaseButton $customType="outline" onClick={handleSelectAll}>
                    {t`Select All`}
                </BaseButton>
                <BaseButton $customType="outline" onClick={handleClearAll}>
                    {t`Clear All`}
                </BaseButton>
            </ButtonsWrapper>
            <BaseInput
                allowClear
                value={searchValue}
                placeholder={searchText}
                prefixIcon={<SearchIcon />}
                onChange={(event) => handleSearch(event.target.value)}
            />
            {filteredSubItems.length > 0 ? (
                <SubItemsSection>
                    {levelTwoItems
                        .map((typeItem) => {
                            const itemsInGroup = filteredSubItems.filter(
                                (subItem: TransformedSubItemForMatrices) => subItem.type === typeItem.value,
                            );
                            if (itemsInGroup.length === 0) return null;

                            return (
                                <ValuesGroupSection key={typeItem.value}>
                                    <ValueGroupHeader>{typeItem.label}</ValueGroupHeader>

                                    {itemsInGroup.map((subItem: TransformedSubItemForMatrices) => (
                                        <SubitemWrapper key={subItem.value}>
                                            <CheckboxLabelWrapper>
                                                <BaseCheckbox
                                                    onChange={(e) =>
                                                        handleCheckboxChange(subItem.value, e.target.checked)
                                                    }
                                                    checked={checkedSubItemValues.has(subItem.value)}
                                                    disabled={!isAllowedSubItem(subItem.value)}
                                                >
                                                    {subItem.label}
                                                </BaseCheckbox>
                                            </CheckboxLabelWrapper>
                                            <SubItemCategoryLabel>
                                                {getSubitemCategoryLabel(subItem.category)}
                                            </SubItemCategoryLabel>
                                        </SubitemWrapper>
                                    ))}
                                </ValuesGroupSection>
                            );
                        })
                        .filter(Boolean)}
                </SubItemsSection>
            ) : (
                <EmptyStateWrapper>
                    <EmptyStateIcon />
                    <EmptyStateText>{t`No subtypes match your search`}</EmptyStateText>
                </EmptyStateWrapper>
            )}

            <SelectedCountSectionVessels>
                {checkedSubItemValues.size} {t`subtype(s) selected`}
                <div>
                    {subItems.length} {t`total`}
                </div>
            </SelectedCountSectionVessels>
            <ButtonsWrapper>
                <BaseButton $customType="outline" onClick={handleCancel}>
                    {t`Cancel`}
                </BaseButton>
                <BaseButton onClick={handleDone}>{t`Done`}</BaseButton>
            </ButtonsWrapper>
        </Wrapper>
    );
};
